import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { SLUG_TO_ES } from "@/components/Navbar";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { StickyBanner } from "@/components/StickyBanner";
import { TrustBar } from "@/components/TrustBar";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { PricingCalculator } from "@/components/PricingCalculator";
import { Guarantee } from "@/components/Guarantee";
import { FinalCTA } from "@/components/FinalCTA";
import { FAQ } from "@/components/FAQ";
import { RecentBookingSocialProof } from "@/components/RecentBookingSocialProof";
import { MobileDiscountPopup } from "@/components/MobileDiscountPopup";
import { LocationContext } from "@/lib/locations";
import { ALL_LA_SUB_REGIONS, LA_SUB_REGIONS } from "@/lib/locations";
import { WaitlistPopup } from "@/components/WaitlistPopup";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Star, CheckCircle2, MapPin, Zap, Calendar,
  ShieldCheck, Home, Sparkles, Award, ChevronRight
} from "lucide-react";

const BOOKING_URL = "/book";

const SERVICES = [
  { icon: <Home className="h-6 w-6 text-primary" />, name: "Standard Cleaning", desc: "Weekly or bi-weekly maintenance for a consistently clean home." },
  { icon: <Sparkles className="h-6 w-6 text-primary" />, name: "Deep Cleaning", desc: "Detailed top-to-bottom clean, perfect for first-time or seasonal cleans." },
  { icon: <ArrowRight className="h-6 w-6 text-primary" />, name: "Move In / Move Out", desc: "Comprehensive cleaning for empty homes during real estate transitions." },
  { icon: <Award className="h-6 w-6 text-primary" />, name: "Post Construction", desc: "Specialized dust and debris removal after renovations or remodels." },
];

const CTABar = ({ locationName }: { locationName: string }) => (
  <div className="sticky top-20 z-40 bg-primary/95 backdrop-blur-sm shadow-lg py-3 px-4 hidden md:block">
    <div className="container flex items-center justify-between">
      <p className="text-primary-foreground font-bold text-sm">
        🏠 Trusted home cleaning across {locationName} — Book in 60 seconds!
      </p>
      <div className="flex gap-3">
        <Button asChild size="sm" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary font-bold">
          <a href={BOOKING_URL} aria-label="Get instant price for LA sub-region"><Zap className="h-4 w-4 mr-1" /> Get Instant Price</a>
        </Button>
        <Button asChild size="sm" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-bold">
          <a href={BOOKING_URL}><Calendar className="h-4 w-4 mr-1" /> Book Now</a>
        </Button>
      </div>
    </div>
  </div>
);

const LASubRegionPage = () => {
  const { subregion } = useParams<{ subregion: string }>();
  const navigate = useNavigate();

  const data = ALL_LA_SUB_REGIONS.find(r => r.slug === subregion);

  useEffect(() => {
    if (localStorage.getItem("langPref") === "es" && subregion && SLUG_TO_ES[subregion]) {
      navigate(SLUG_TO_ES[subregion], { replace: true });
    }
  }, [navigate, subregion]);

  if (!data) return <Navigate to="/locations/los-angeles" replace />;

  const locationCtx = {
    slug: data.slug,
    name: data.shortName,
    type: 'city' as const,
    regionSlug: 'los-angeles' as const,
  };

  return (
    <LocationContext.Provider value={locationCtx}>
      <SEO
        title={`#1 House Cleaning in ${data.name} | Uplevel Cleaning Los Angeles`}
        description={`Top-rated home cleaning services in ${data.name}. Serving ${data.cities.join(", ")}. Instant pricing, 60-second booking, and 200% satisfaction guarantee.`}
        keywords={`house cleaning ${data.name}, maid service ${data.cities[0]}, home cleaning ${data.cities[1] || data.cities[0]}, deep cleaning ${data.name}, move out cleaning ${data.cities[0]}, uplevel cleaning los angeles`}
        url={`https://uplevelcleaning.com/locations/la/${data.slug}`}
      />
      <WaitlistPopup />
      <RecentBookingSocialProof />
      <MobileDiscountPopup />
      <StickyBanner />
      <Navbar />
      <CTABar locationName={data.shortName} />

      <main className="flex-1">
        {/* HERO */}
        <section className="relative min-h-[600px] flex items-center overflow-hidden bg-background">
          <div className="absolute inset-0 z-0">
            <img
              srcSet="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=800&q=80 800w, https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80 1920w"
              sizes="(max-width: 768px) 800px, 1920px"
              src="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80"
              alt={`Home Cleaning in ${data.name}`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-transparent" />
          </div>

          <div className="container relative z-10 pt-16 pb-16 lg:pt-24 lg:pb-24">
            <div className="max-w-2xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/locations/los-angeles" className="hover:text-primary transition-colors">Los Angeles County</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-foreground font-semibold">{data.shortName}</span>
              </nav>

              <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full w-fit mb-6">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-primary">{data.shortName}, Los Angeles County</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-4 text-foreground leading-[1.1]">
                House Cleaning in <span className="text-primary">{data.name}</span>
              </h1>
              <p className="text-xl font-semibold text-foreground mb-2">
                Get your exact price in seconds.
              </p>
              <p className="text-base text-muted-foreground mb-8 max-w-lg">
                {data.description}. No hidden fees. Upfront pricing. 200% Satisfaction guaranteed.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button asChild size="lg" className="h-14 px-8 rounded-2xl bg-primary text-primary-foreground hover:bg-primary-hover text-lg font-bold shadow-xl group">
                  <a href={BOOKING_URL}>
                    <Calendar className="mr-2 h-5 w-5" /> Book Now
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 px-8 rounded-2xl border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary text-lg font-bold bg-background/80 backdrop-blur-sm">
                  <a href={BOOKING_URL} aria-label="Get instant price from hero section"><Zap className="mr-2 h-5 w-5" /> Get Instant Price</a>
                </Button>
              </div>

              <div className="flex items-center gap-4 text-sm font-medium text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> No commitment</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Cancel anytime</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 200% Guarantee</span>
              </div>
            </div>
          </div>
        </section>

        <TrustBar />

        {/* CITIES SERVED */}
        <section className="py-16 bg-muted/30">
          <div className="container">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-3">Cities We Serve in {data.shortName}</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">Professional home cleaning available across every neighborhood in {data.shortName}.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {data.cities.map(city => (
                <Link
                  key={city}
                  to={`/locations/${city.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-2 px-5 py-3 rounded-full border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all font-semibold text-sm shadow-sm group"
                >
                  <MapPin className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Services Available in {data.shortName}</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">From routine maintenance to deep cleans and move-outs, we handle it all.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SERVICES.map((s, i) => (
                <div key={i} className="p-6 rounded-2xl border bg-card hover:shadow-lg hover:border-primary/40 transition-all group">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    {s.icon}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{s.name}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Button asChild size="lg" className="h-14 px-10 rounded-2xl font-bold text-lg">
                <a href={BOOKING_URL}><Calendar className="mr-2 h-5 w-5" /> Book Now</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-10 rounded-2xl font-bold text-lg border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary">
                <a href={BOOKING_URL} aria-label="Get instant price for services"><Zap className="mr-2 h-5 w-5" /> Get Instant Price</a>
              </Button>
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="py-20 bg-muted/20">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Why {data.shortName} Homeowners Choose Uplevel</h2>
                <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                  We understand the unique needs of {data.shortName} homes — from beachside condos to sprawling estates. Our professional team is trained, insured, and dedicated to delivering a spotless clean every single time.
                </p>
                <ul className="space-y-4 mb-10">
                  {data.highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-3 font-semibold">
                      <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="font-bold rounded-xl">
                    <a href={BOOKING_URL}><Calendar className="mr-2 h-4 w-4" /> Book Now</a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="font-bold rounded-xl border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary">
                    <a href={BOOKING_URL} aria-label="Get instant price for custom plans"><Zap className="mr-2 h-4 w-4" /> Get Instant Price</a>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: "500+", label: "Homes Cleaned in LA" },
                  { stat: "4.9★", label: "Average Rating" },
                  { stat: "200%", label: "Satisfaction Guarantee" },
                  { stat: "60s", label: "Average Booking Time" },
                ].map((item, i) => (
                  <div key={i} className="p-8 rounded-2xl bg-background border shadow-sm text-center hover:shadow-md hover:border-primary/30 transition-all">
                    <p className="text-4xl font-extrabold text-primary mb-2">{item.stat}</p>
                    <p className="text-sm font-semibold text-muted-foreground">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* LOCAL TESTIMONIALS */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What {data.shortName} Customers Say</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">Real reviews from homeowners right in your neighborhood.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.testimonials.map((t, i) => (
                <div key={i} className="p-8 rounded-2xl border bg-card shadow-sm hover:shadow-lg hover:border-primary/30 transition-all">
                  <div className="flex text-yellow-400 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-current" />)}
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed italic">"{t.quote}"</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold">{t.name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.city}</p>
                    </div>
                    <div className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full font-semibold border border-green-200">
                      <CheckCircle2 className="h-3 w-3" /> Verified
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <WhyChooseUs />

        {/* MID-PAGE CTA */}
        <section className="py-16 bg-primary">
          <div className="container text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-primary-foreground mb-4">
              Ready for a Spotless Home in {data.shortName}?
            </h2>
            <p className="text-primary-foreground/80 mb-8 text-lg max-w-xl mx-auto">
              Book in 60 seconds. No hidden fees. 200% satisfaction guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="h-14 px-10 text-lg font-bold rounded-xl bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                <a href={BOOKING_URL}><Calendar className="mr-2 h-5 w-5" /> Book Now</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold rounded-xl border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <a href={BOOKING_URL} aria-label="Get instant price from bottom section"><Zap className="mr-2 h-5 w-5" /> Get Instant Price</a>
              </Button>
            </div>
            <div className="mt-6 flex items-center justify-center gap-6 text-primary-foreground/70 text-sm flex-wrap">
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Fully Insured</span>
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Background Checked</span>
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> 200% Guarantee</span>
            </div>
          </div>
        </section>

        <FrequencyDiscounts />
        <PricingCalculator />

        {/* OTHER LA SUB-REGIONS */}
        <section className="py-20 bg-muted/20">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Explore Other LA Areas We Serve</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">We cover all of Los Angeles County. Find your specific neighborhood below.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {ALL_LA_SUB_REGIONS.filter(r => r.slug !== data.slug).map(region => (
                <Link
                  key={region.slug}
                  to={`/locations/la/${region.slug}`}
                  className="p-6 rounded-2xl border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all group shadow-sm"
                >
                  <MapPin className="h-6 w-6 text-primary group-hover:text-primary-foreground mb-3 transition-colors" />
                  <h3 className="font-bold text-lg mb-2">{region.shortName}</h3>
                  <p className="text-sm text-muted-foreground group-hover:text-primary-foreground/80 transition-colors line-clamp-2">{region.cities.join(", ")}</p>
                  <div className="mt-4 flex items-center gap-1 text-primary group-hover:text-primary-foreground font-semibold text-sm transition-colors">
                    View Area <ArrowRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary font-bold">
                <Link to="/locations/los-angeles">View All Los Angeles Cities <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        <Guarantee />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </LocationContext.Provider>
  );
};

export default LASubRegionPage;
